import moment from 'moment';

/**
 * Determines whether a source should be broadcasting or not.
 *
 * @param  {Object} source An object representing the source (a BoxCaster, Broadcaster app, or RTMP
 *                         channel). BoxCaster and RTMP sources will have a `next_broadcast_at`
 *                         member; Broadcaster sources will have `details.starts_at`.
 * @return {Boolean}       `true` if the source should be broadcasting; `false` if it should not
 *                         (or the parameters don't make sense).
 */
function sourceShouldBeBroadcasting(source) {
  if (source.next_broadcast_at) {
    return Date.now() > moment(source.next_broadcast_at);
  } else if (source.details && source.details.starts_at) {
    return Date.now() > moment(source.details.starts_at);
  } else {
    return false;
  }
}

/**
 * Returns a string describing the source type.
 *
 * @param  {Object} source An object representing the source (a BoxCaster, Broadcaster app, RTMP
 *                         channel, or Producer). RTMP sources will have a `server_url` member; Broadcaster
 *                         sources will have `is_broadcaster`. Producer will have `is_producer`. All others
 *                         are assumed to be BoxCasters.
 * @return {String}        `'rtmp'` for RTMP sources; `'broadcaster'` for Broadcaster sources;
 *                         `'boxcaster'` for BoxCaster sources; `null` if the parameters don't make
 *                         sense. `'producer'` for producer
 */
export function sourceType(source) {
  if (!source) {
    return null;
  } else if (source.server_url) {
    return 'rtmp';
  } else if (source.is_broadcaster) {
    return 'broadcaster';
  } else if (source.is_producer) {
    return 'producer';
  } else {
    return 'boxcaster';
  }
}

/**
 * Returns a string describing the source status (if it is an RTMP source).
 *
 * @param  {Object} source                 An object representing the RTMP source.
 * @param  {Object} broadcastsByStaticRTMP A hash of broadcasts from static RTMP sources, keyed by
 *                                         static RTMP ID (used to determine if an RTMP source is
 *                                         actually broadcasting or simply streaming to the proxy).
 * @return {String}                        `'offline'` if there are no diagnostics, or the
 *                                         diagnostics indicate the source is not connected;
 *                                         `'connected'` if the source is connected and
 *                                         broadcasting, but there doesn't appear to be an active
 *                                         broadcast using that source ID; `'broadcasting'` in all
 *                                         other cases.
 */
export function sourceRtmpStatus(source, broadcastsByStaticRTMP) {
  // broadcastsByStaticRTMP may not be provided, e.g., in unit tests. In that case, initialize it
  // to an empty object.
  if (!broadcastsByStaticRTMP) {
    broadcastsByStaticRTMP = {};
  }

  if (source.diagnostics === undefined || !source.diagnostics.connected) {
    return 'offline';
  }
  if (!source.diagnostics.broadcasting || broadcastsByStaticRTMP[source.id] === undefined) {
    return 'connected';
  }
  return 'broadcasting';
}

function isAlphanum(charCode) {
  // 46 == '.'
  // 48-57 == '0'-'9'
  return charCode == 46 || (charCode >= 48 && charCode <= 57);
}

/**
 * Sorts an array of sources in a sensible, human-readable way. Generally, sources with somehting
 * important going on (should be broadcasting, is broadcasting, etc.) are elevated to the top,
 * while the rest are sorted alphanumerically and case-insensitively. The sources are sorted in
 * place.
 *
 * The algorithm used here is loosely based on the one described at
 * http://www.davekoelle.com/alphanum.html, but modified for this specific use case.
 *
 * @param  {Object} sources                A list of sources to be sorted.
 * @param  {Object} broadcastsByStaticRTMP A hash of broadcasts from static RTMP sources, keyed by
 *                                         static RTMP ID (used to determine if an RTMP source is
 *                                         actually broadcasting or simply streaming to the proxy).
 */
export function sortSources(sources, broadcastsByStaticRTMP) {
  // broadcastsByStaticRTMP may not be provided, e.g., in unit tests. In that case, initialize it
  // to an empty object.
  if (!broadcastsByStaticRTMP) {
    broadcastsByStaticRTMP = {};
  }

  // First, chunk source names into a new property on each source
  for (let z = 0, t; (t = sources[z]); z++) {
    t._chunkedName = new Array();

    let x = 0,
      y = -1,
      n = 0,
      i,
      j;
    while ((i = (j = t.name.charAt(x++)).charCodeAt(0))) {
      const m = isAlphanum(i);
      if (m !== n) {
        t._chunkedName[++y] = '';
        n = m;
      }
      t._chunkedName[y] += j;
    }
  }

  // (De-)escalate as needed by putting a new chunk in front of the chunked name
  for (let z = 0, source; (source = sources[z]); z++) {
    if (sourceType(source) == 'boxcaster') {
      const order = ['broadcasting', 'preparing_broadcast', 'ready'].indexOf(source.status);
      if (order >= 0) {
        source._chunkedName.unshift(`AAA-${order + 1}`);
      } else if (sourceShouldBeBroadcasting(source)) {
        source._chunkedName.unshift('AAA-0'); // error state, bubble it up
      } else {
        source._chunkedName.unshift('ZZZ');
      }
    } else if (sourceType(source) == 'rtmp') {
      const s = sourceRtmpStatus(source, broadcastsByStaticRTMP);
      if (s == 'broadcasting') {
        source._chunkedName.unshift('AAA-1');
      } else if (s == 'connected') {
        source._chunkedName.unshift('AAA-3');
      } else {
        source._chunkedName.unshift('ZZZ');
      }
    } else if (sourceType(source) == 'broadcaster') {
      if (sourceShouldBeBroadcasting(source)) {
        source._chunkedName.unshift('AAA-0'); // push Broadcaster up when it should be broadcasting
      } else {
        source._chunkedName.unshift('ZZZ');
      }
    }
  }

  // Now sort by the chunked name (alphanumeric portions are compared as numbers)
  sources.sort(function (a, b) {
    for (let x = 0, aa, bb; (aa = a._chunkedName[x]) && (bb = b._chunkedName[x]); x++) {
      aa = aa.toLowerCase();
      bb = bb.toLowerCase();
      if (aa !== bb) {
        var c = Number(aa),
          d = Number(bb);
        if (c == aa && d == bb) {
          return c - d;
        } else {
          return aa > bb ? 1 : -1;
        }
      }
    }
    return a._chunkedName.length - b._chunkedName.length;
  });

  // Now delete our temporary property
  for (let z = 0; z < sources.length; z++) {
    delete sources[z]._chunkedName;
  }
}



// WEBPACK FOOTER //
// ./app/utils/sources.js